// DailyLoginModal.js
import React, { useState, useEffect, useContext } from 'react';
import styled, { css } from 'styled-components';
import { RichCoinIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import { useUserData } from '../contexts/UserDataContext';
import { TelegramUserContext } from '../App';
import GenericModal from './GenericModal';

// Styled components specific to DailyLoginModal content
// Some styles from original ModalContainer are moved here to a ContentHost
const ContentHost = styled.div`
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  overflow-y: auto !important;
  box-sizing: border-box !important;
  padding: 0; /* Let internal elements define padding */
  color: white; /* Default text color for this modal's content */
`;

// Original HeaderSection, ModalTitle, CloseButton - adapted for use within GenericModal's structure
// Or ensure GenericModal's header can be styled for white text
const CustomHeaderSection = styled.div`
  display: flex;
  justify-content: flex-start; /* Aligns title and close button container to the left */
  align-items: center;
  padding: 20px 20px 5px;
  width: 100%;
  max-width: 500px; /* Max width for the content area */
  margin: 0 auto; /* Center the header content */
  position: relative;
  
  @media (max-width: 360px) {
    padding: 16px 16px 5px;
  }
`;

const CustomModalTitle = styled.h2`
  font-size: 40px;
  color: white; /* Specific to this modal */
  text-align: left;
  font-weight: 600;
  margin: 0;
  padding: 0;
  line-height: 1.1;
  
  @media (max-width: 360px) {
    font-size: 34px;
  }
`;

const CustomCloseButton = styled.button`
  background: none;
  border: none;
  color: white; /* Specific to this modal */
  font-size: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 10px;
  margin-top: 2px;
  z-index: 100;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }

  @media (max-width: 360px) {
    font-size: 34px;
    margin-left: 8px;
  }
`;


const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 20px;
  width: calc(100% - 40px);
  max-width: 500px;
  margin: 10px auto 20px auto;
`;

const DayCard = styled.div`
  background-color: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : '#2A2A2A'};
  border: ${props => props.$active ? '2px solid #7289DA' : '2px solid transparent'};
  border-radius: 16px;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transition: all 0.2s ease;
  
  &:active {
    transform: ${props => props.$active ? 'scale(0.97)' : 'none'};
  }
  
  @media (max-width: 360px) {
    padding: 10px 8px;
  }
`;

const DayNumber = styled.div`
  font-size: 15px;
  color: white;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 360px) {
    font-size: 14px;
  }
`;

const RewardAmount = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: white;
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 360px) {
    font-size: 22px;
  }
`;

const LastDayCard = styled(DayCard)`
  grid-column: 1 / span 2;
  background-color: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'rgba(60, 60, 60, 0.5)'};
  border: ${props => props.$active ? '2px solid #FFC400' : '2px solid transparent'};
  padding: 16px 10px;
  
  ${RewardAmount} {
    font-size: 30px;
  }
  
  @media (max-width: 360px) {
    padding: 12px 10px;
  }
`;

const CoinContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1px;
  transform: translateY(-0.5px);
`;

const CheckMarkIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #7289DA;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 3px;
  
  svg {
    width: 15px;
    height: 15px;
  }
  
  @media (max-width: 360px) {
    width: 20px;
    height: 20px;
    
    svg {
      width: 13px;
      height: 13px;
    }
  }
`;

const ClaimButton = styled.button`
  background-color: ${props => props.$active ? '#7C7C7C' : '#6E6E6E'};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 500;
  padding: 14px;
  width: calc(100% - 40px);
  max-width: 500px;
  margin: 10px auto;
  cursor: ${props => props.$active ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  display: block;
  position: relative;
  
  &:active {
    transform: ${props => props.$active ? 'scale(0.98)' : 'none'};
    background-color: ${props => props.$active ? '#8C8C8C' : '#6E6E6E'};
  }
  
  &:hover {
    background-color: ${props => props.$active ? '#8C8C8C' : '#6E6E6E'};
  }
  
  @media (max-width: 360px) {
    padding: 12px;
    font-size: 16px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  z-index: 10;
`;

const DailyLoginModal = ({ isOpen, onClose }) => {
  const telegramUser = useContext(TelegramUserContext);
  const { getDailyLoginData, claimDailyReward } = useUserData();
  
  const [currentDay, setCurrentDay] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isClaimLoading, setIsClaimLoading] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [claimError, setClaimError] = useState(null);
  
  const initialDailyRewards = React.useMemo(() => [
    { day: 1, amount: 100, claimed: false }, { day: 2, amount: 200, claimed: false },
    { day: 3, amount: 300, claimed: false }, { day: 4, amount: 400, claimed: false },
    { day: 5, amount: 500, claimed: false }, { day: 6, amount: 600, claimed: false },
    { day: 7, amount: 10000, claimed: false }
  ], []);
  
  const [dailyRewardsState, setDailyRewardsState] = useState(initialDailyRewards);

  useEffect(() => {
    const loadDailyLoginData = async () => {
      if (!telegramUser?.id || !isOpen) {
        if (!isOpen) { // Reset when closing
            setDailyRewardsState(initialDailyRewards);
            setCurrentDay(1);
            setCanClaim(false);
            setClaimError(null);
        }
        setIsLoading(!isOpen); // Set loading to false if not open
        return;
      }
      
      setIsLoading(true);
      try {
        const data = await getDailyLoginData(telegramUser.id);
        if (data) {
          setCurrentDay(data.currentDay);
          setCanClaim(data.canClaimToday);
          const updatedRewards = initialDailyRewards.map(reward => {
            // A day is claimed if it's before the current day,
            // OR if it's the current day AND the user cannot claim today.
            const isClaimed = reward.day < data.currentDay || (reward.day === data.currentDay && !data.canClaimToday);
            return { ...reward, claimed: isClaimed };
          });
          setDailyRewardsState(updatedRewards);
        } else {
           // Handle case where data might be null or undefined from backend
           setDailyRewardsState(initialDailyRewards); // Reset to initial
           setCanClaim(false); // Default to cannot claim
        }
      } catch (error) {
        console.error('Error loading daily login data:', error);
        setClaimError('Failed to load daily login data.');
        setDailyRewardsState(initialDailyRewards); // Reset on error
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDailyLoginData();
  }, [telegramUser?.id, getDailyLoginData, isOpen, initialDailyRewards]);
  
  const handleClaim = async () => {
    if (!telegramUser?.id || !canClaim || isClaimLoading) return;
    
    setIsClaimLoading(true);
    setClaimError(null);
    
    try {
      const result = await claimDailyReward(telegramUser.id);
      if (result.success) {
        const updatedRewards = dailyRewardsState.map(reward =>
          reward.day === currentDay ? { ...reward, claimed: true } : reward
        );
        setDailyRewardsState(updatedRewards);
        setCanClaim(false);
        setTimeout(() => { onClose(); }, 1500);
      } else {
        setClaimError(result.error || 'Failed to claim reward');
      }
    } catch (error) {
      console.error('Error claiming reward:', error);
      setClaimError('An unexpected error occurred');
    } finally {
      setIsClaimLoading(false);
    }
  };

  // Custom styles for GenericModal to match DailyLoginModal's appearance
  const genericModalCustomContainerStyles = css`
    background-color: transparent !important;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important; /* Override GenericModal's max-width */
    border-radius: 0 !important; /* Override GenericModal's border-radius */
    padding: 0 !important; /* Override GenericModal's padding */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* Ensure it doesn't intercept clicks meant for overlay close */
    pointer-events: none;
    & > * {
        pointer-events: auto; /* Re-enable pointer events for children */
    }
  `;
  
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="" /* Title is handled by CustomHeaderSection */
      hideCloseButton={true} /* Close button is handled by CustomHeaderSection */
      zIndex={999999}
      customContainerStyles={genericModalCustomContainerStyles}
      contentPadding="0" /* ContentHost handles its own layout */
      disableBodyScroll={true} // Keep body scroll disabled
    >
      <ContentHost onClick={(e) => e.stopPropagation()}> {/* This click stop is important */}
        <CustomHeaderSection>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomModalTitle>Daily Login</CustomModalTitle>
            <CustomCloseButton onClick={onClose}>×</CustomCloseButton>
          </div>
        </CustomHeaderSection>
        
        <DaysGrid>
          {dailyRewardsState.slice(0, 6).map((reward, index) => (
            <DayCard 
              key={index} 
              $active={reward.day === currentDay || reward.claimed}
            >
              <DayNumber>
                Day {reward.day}
                {reward.claimed && (
                  <CheckMarkIcon>
                    <Icon icon="lets-icons:check-fill" width="24" height="24" color="white" />
                  </CheckMarkIcon>
                )}
              </DayNumber>
              <RewardAmount>
                +{reward.amount} 
                <CoinContainer>
                  <RichCoinIcon width="22" height="22" />
                </CoinContainer>
              </RewardAmount>
            </DayCard>
          ))}
          
          <LastDayCard $active={dailyRewardsState[6].day === currentDay || dailyRewardsState[6].claimed}>
            <DayNumber>
              Day {dailyRewardsState[6].day}
              {dailyRewardsState[6].claimed && (
                <CheckMarkIcon>
                  <Icon icon="lets-icons:check-fill" width="24" height="24" color="white" />
                </CheckMarkIcon>
              )}
            </DayNumber>
            <RewardAmount>
              +{dailyRewardsState[6].amount}
              <CoinContainer>
                <RichCoinIcon width="22" height="22" />
              </CoinContainer>
            </RewardAmount>
          </LastDayCard>
        </DaysGrid>
        
        {claimError && (
          <div style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>
            {claimError}
          </div>
        )}
        
        <ClaimButton 
          $active={canClaim && !isLoading}
          onClick={canClaim && !isLoading ? handleClaim : null}
          disabled={isClaimLoading || isLoading} // Also disable if initial data is loading
        >
          {isLoading ? 'Loading...' : isClaimLoading ? (
            <>
              <Icon icon="eos-icons:loading" width="20" height="20" />
              Claiming...
            </>
          ) : canClaim ? (
            "Claim reward"
          ) : (
            dailyRewardsState.find(r => r.day === currentDay)?.claimed ? "Claimed Today" : "Come back tomorrow"
          )}
          
          {isClaimLoading && (
            <LoadingOverlay>
              <Icon icon="eos-icons:loading" width="32" height="32" color="white" />
            </LoadingOverlay>
          )}
        </ClaimButton>
      </ContentHost>
    </GenericModal>
  );
};

export default DailyLoginModal;
